import { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import "@/lib/leafletIconFix";
import L from "leaflet";
import { toast } from "sonner";
import {
  Search, Filter, MapPin, MousePointerClick, ChevronDown, MapPinned,
  SlidersHorizontal, CircleDot, Hash, Tag, Loader2, Sparkles, CreditCard,
} from "lucide-react";
import { Button, Input, Card } from "@/components/kit";
import CityCombobox from "@/components/CityCombobox";
import ScrapeResults from "@/components/ScrapeResults";
import ScrapeHistory from "@/components/ScrapeHistory";
import useScrape from "@/lib/useScrape";

const DEFAULT_CENTER = [-6.2088, 106.8456];
const CATEGORIES = ["", "Restoran", "Kafe", "Salon", "Barbershop", "Bengkel", "Klinik",
  "Apotek", "Toko", "Laundry", "Hotel", "Catering", "Gym", "Spa", "Minimarket", "Sekolah"];

function useGeolocate() {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (p) => setPos([p.coords.latitude, p.coords.longitude]),
      () => {}
    );
  }, []);
  return pos;
}

export default function GoogleMapsScraper() {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);

  const geolocated = useGeolocate();

  const [keyword, setKeyword] = useState("");
  const [city, setCity] = useState("Jakarta");
  const [coord, setCoord] = useState(DEFAULT_CENTER);
  const [radius, setRadius] = useState(5);
  const [count, setCount] = useState(20);
  const [category, setCategory] = useState("");
  const [openNow, setOpenNow] = useState(false);
  const [scrapeEmail, setScrapeEmail] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ only_phone: false, no_website: false, min_rating: 0 });

  const { results, setResults, selected, loading, saving, run, toggle, save, selectedLeads, suggestions, progress } = useScrape("google_maps");

  // init map
  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, { zoomControl: false }).setView(DEFAULT_CENTER, 12);
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      maxZoom: 19, attribution: "© OpenStreetMap contributors, © CARTO", subdomains: "abcd",
    }).addTo(map);
    L.control.zoom({ position: "bottomright" }).addTo(map);

    const pulseIcon = L.divIcon({
      className: "",
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      html: `<div style="width:24px;height:24px;position:relative">
        <div style="position:absolute;inset:0;border-radius:50%;background:rgba(14,165,233,0.25);animation:live-pulse 1.8s ease-in-out infinite"></div>
        <div style="position:absolute;top:5px;left:5px;width:14px;height:14px;background:#0ea5e9;border:2.5px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(14,165,233,0.4)"></div>
      </div>`,
    });

    markerRef.current = L.marker(DEFAULT_CENTER, { draggable: true, icon: pulseIcon }).addTo(map);
    circleRef.current = L.circle(DEFAULT_CENTER, {
      radius: 5000, color: "#0ea5e9", weight: 1.5, fillColor: "#0ea5e9", fillOpacity: 0.08,
      dashArray: "6 4",
    }).addTo(map);

    markerRef.current.on("drag", () => {
      const ll = markerRef.current.getLatLng();
      circleRef.current.setLatLng(ll);
      setCoord([ll.lat, ll.lng]);
    });
    map.on("click", (e) => {
      markerRef.current.setLatLng(e.latlng);
      circleRef.current.setLatLng(e.latlng);
      setCoord([e.latlng.lat, e.latlng.lng]);
    });
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(mapRef.current);
    mapObj.current = map;
    return () => { ro.disconnect(); mapObj.current = null; map.remove(); };
  }, []);

  // geolocate
  useEffect(() => {
    if (!geolocated || !mapObj.current) return;
    setCoord(geolocated);
    mapObj.current.setView(geolocated, 12);
    markerRef.current?.setLatLng(geolocated);
    circleRef.current?.setLatLng(geolocated);
  }, [geolocated]);

  // radius circle
  useEffect(() => {
    if (!circleRef.current || !mapObj.current) return;
    circleRef.current.setRadius(radius * 1000);
    const t = setTimeout(() => {
      if (mapObj.current && circleRef.current) {
        mapObj.current.flyToBounds(circleRef.current.getBounds(), { padding: [40, 40], maxZoom: 14, duration: 0.5 });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [radius]);

  const onCitySelect = (c) => {
    setCity(c.name || "");
    if (c.lat && c.lng && mapObj.current) {
      const ll = [c.lat, c.lng];
      setCoord(ll);
      markerRef.current.setLatLng(ll);
      circleRef.current.setLatLng(ll);
      mapObj.current.flyToBounds(circleRef.current.getBounds(), { padding: [40, 40], maxZoom: 14, duration: 0.8 });
    }
  };

  const onRerun = (h) => {
    setKeyword(h.keyword);
    setCity(h.city);
    if (h.radius_km) setRadius(h.radius_km);
    run({ keyword: h.keyword, city: h.city, lat: coord[0], lng: coord[1], radius_km: h.radius_km || radius, count, category, open_now: openNow, scrape_email: scrapeEmail, only_phone: filters.only_phone, no_website: filters.no_website, min_rating: Number(filters.min_rating) });
  };

  const onRun = () => {
    if (!keyword.trim()) { toast.error("Masukkan keyword pencarian"); return; }
    if (!city.trim()) { toast.error("Pilih atau ketik kota"); return; }
    run({
      keyword, city, lat: coord[0], lng: coord[1], radius_km: radius, count,
      category, open_now: openNow, scrape_email: scrapeEmail,
      only_phone: filters.only_phone, no_website: filters.no_website, min_rating: Number(filters.min_rating),
    });
  };

  const creditCost = scrapeEmail ? count * 2 : count;

  return (
    <div className="space-y-5 animate-float-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-50 to-cyan-50 grid place-items-center shadow-sm">
            <MapPinned className="w-5.5 h-5.5 text-sky-500" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Scraper Google Maps</h1>
            <p className="text-slate-400 mt-0.5 text-xs">Cari bisnis berdasarkan keyword, lokasi & kategori.</p>
          </div>
        </div>
      </div>

      {/* Main content: sidebar + map */}
      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-4 items-start">

        {/* Left sidebar: form */}
        <div className="space-y-3 order-2 xl:order-1">

          {/* Search card */}
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <Search className="w-3.5 h-3.5" /> Pencarian
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Keyword</label>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="scrape-keyword" placeholder="restoran, salon, bengkel..." value={keyword}
                  onChange={(e) => setKeyword(e.target.value)} className="pl-9 h-10 text-sm rounded-xl" onKeyDown={(e) => e.key === "Enter" && onRun()} />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Lokasi</label>
              <CityCombobox value={city} onSelect={onCitySelect} />
            </div>
            <Button data-testid="scrape-run" onClick={onRun} loading={loading} size="lg" className="w-full h-11 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white font-semibold shadow-lg shadow-sky-500/20 transition-all duration-200">
              <Search className="w-4 h-4" /> Cari Sekarang
            </Button>
          </Card>

          {/* Parameters */}
          <Card className="p-4 space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Parameter
            </div>

            {/* Radius */}
            <div>
              <label className="flex justify-between text-[11px] font-semibold text-slate-400 mb-1.5">
                <span className="flex items-center gap-1"><CircleDot className="w-3 h-3" /> RADIUS</span>
                <span className="flex items-center gap-1 text-sky-500">
                  <input type="number" min="1" max="50" value={radius}
                    onChange={(e) => setRadius(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                    className="w-10 text-center bg-transparent border-b border-sky-200 outline-none text-xs font-bold" />
                  km
                </span>
              </label>
              <input type="range" min="1" max="50" value={radius} onChange={(e) => setRadius(Number(e.target.value))}
                className="w-full h-1.5 rounded-full" data-testid="radius-slider" />
            </div>

            {/* Jumlah hasil */}
            <div>
              <label className="flex justify-between text-[11px] font-semibold text-slate-400 mb-1.5">
                <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> JUMLAH HASIL</span>
                <span className="text-sky-500 font-bold">{count}</span>
              </label>
              <input type="range" min="5" max="100" step="5" value={count} onChange={(e) => setCount(Number(e.target.value))}
                className="w-full h-1.5 rounded-full" />
            </div>

            {/* Kategori */}
            <div>
              <label className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 mb-1.5">
                <Tag className="w-3 h-3" /> KATEGORI
              </label>
              <select data-testid="category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c || "Semua kategori"}</option>)}
              </select>
            </div>
          </Card>

          {/* Advanced filters */}
          <Card className="p-4 space-y-3">
            <button data-testid="toggle-filters" onClick={() => setShowFilters((v) => !v)}
              className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-sky-500 uppercase tracking-wider transition w-full">
              <Filter className="w-3.5 h-3.5" /> Filter Lanjutan
              <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform duration-200 ${showFilters ? "rotate-180" : ""}`} />
            </button>

            {showFilters && (
              <div className="flex flex-col gap-2.5 pt-1 animate-float-up" data-testid="advanced-filters">
                <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer group">
                  <input type="checkbox" checked={openNow} onChange={(e) => setOpenNow(e.target.checked)} />
                  <span className="group-hover:text-slate-800 transition">Buka sekarang</span>
                </label>
                <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer group">
                  <input type="checkbox" checked={filters.only_phone} onChange={(e) => setFilters({ ...filters, only_phone: e.target.checked })} />
                  <span className="group-hover:text-slate-800 transition">Punya telepon</span>
                </label>
                <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer group">
                  <input type="checkbox" checked={filters.no_website} onChange={(e) => setFilters({ ...filters, no_website: e.target.checked })} />
                  <span className="group-hover:text-slate-800 transition">Tanpa website</span>
                </label>
                <label className="flex items-center gap-2.5 text-sm text-slate-600">
                  Min rating
                  <select value={filters.min_rating} onChange={(e) => setFilters({ ...filters, min_rating: e.target.value })}
                    className="rounded-lg border border-slate-200 px-2 py-1 text-xs focus:border-sky-400 outline-none">
                    <option value="0">Semua</option><option value="3">3.0+</option><option value="4">4.0+</option><option value="4.5">4.5+</option>
                  </select>
                </label>
                <label data-testid="scrape-email-toggle"
                  className="flex items-center gap-2.5 text-sm rounded-xl border border-violet-200 bg-violet-50/70 px-3 py-2 cursor-pointer transition hover:bg-violet-50">
                  <input type="checkbox" checked={scrapeEmail} onChange={(e) => setScrapeEmail(e.target.checked)} />
                  <div className="flex flex-col">
                    <span className="font-semibold text-violet-700 text-sm">Sertakan Email</span>
                    <span className="text-[10px] text-violet-400 flex items-center gap-1"><CreditCard className="w-2.5 h-2.5" /> 2× kredit</span>
                  </div>
                </label>
              </div>
            )}
          </Card>

          {/* Credit cost indicator */}
          <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400 py-1">
            <CreditCard className="w-3 h-3" />
            Estimasi: <span className="font-bold text-sky-500">{creditCost}</span> kredit
          </div>

          <ScrapeHistory source="google_maps" onRerun={onRerun} />
        </div>

        {/* Right: Map */}
        <div className="space-y-3 order-1 xl:order-2 xl:sticky xl:top-4">
          <Card className="p-2.5 overflow-hidden">
            {/* Map info bar */}
            <div className="flex items-center justify-between px-3 pb-2">
              <span className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 live-dot" />
                {coord[0].toFixed(4)}, {coord[1].toFixed(4)}
                <span className="text-slate-300 font-normal">·</span>
                <span className="text-sky-400 font-bold">{radius} km</span>
              </span>
              <span className="text-[10px] text-slate-400 flex items-center gap-1">
                <MousePointerClick className="w-3 h-3" /> Geser pin atau klik peta
              </span>
            </div>
            {/* Map container */}
            <div className="relative h-[460px] xl:h-[calc(100vh-220px)] min-h-[400px] max-h-[640px] w-full rounded-xl overflow-hidden border border-slate-100">
              <div ref={mapRef} className="absolute inset-0" data-testid="scrape-map" />
              {/* Loading overlay */}
              {loading && (
                <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-[500] flex flex-col items-center justify-center gap-2">
                  <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
                  <span className="text-xs font-semibold text-slate-500">Mencari di Google Maps…</span>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Results */}
      <ScrapeResults source="google_maps" results={results} setResults={setResults} loading={loading} selected={selected} toggle={toggle}
        selectedLeads={selectedLeads} save={save} saving={saving} progress={progress} suggestions={suggestions} />
    </div>
  );
}
